/**
 * ROC 7
 * Copyright 2016 ROC Commerce
 * 
 * Any modifications required of core files should be handled in supplemental files 
 * or duplicates of said core files. This is to assist in upgrade paths.
 */


// # Handlebars additional helpers
define('global/HandlebarsUtils',[
    'jquery',
    'lodash',
    './Constants',
    './Core',
    './CreditCard',
    './Logger',
    './UI/all',
    'handlebars',
    'plugins/accounting.min',
    'plugins/moment-timezone.min'
], function ($, _, Constants, Core, CreditCard, Logger, UI, Handlebars, accounting, moment) {

    Logger.debug('Loading Handlebars Helpers.');

    var cfg = window.RocConfig;
    var packagePath = cfg.cms.packagePath;
    var defaultCountryCode = cfg.defaultCountryCode;
    var enableInternationalShipping = cfg.shipping.enableInternationalShipping;
    var counters = {};
    var timezone = cfg.time.timezone || 'America/Chicago'; // default timezone

    Handlebars.registerHelper('counterStart', function (name) {
        counters[name] = 1;
    });

    Handlebars.registerHelper('counterIncrement', function (name) {
        if (counters.hasOwnProperty(name)) {
            counters[name]++;
        }
    });

    Handlebars.registerHelper('counterGet', function (name) {
        return counters[name];
    });

    Handlebars.registerHelper('formatRecipientLabel', function(recipient) {
        return UI.formatRecipientLabel(recipient);
    });

    Handlebars.registerHelper('hasAnyShippingRecipient', function (order, options) {
        var result = _.some(order.recipients, { itemsDeliveryType: 'Shipping' });
        return result ? options.fn(this) : options.inverse(this);
    });

    /**
     * HELPER: math
     * Usage: {{math price "*" quantity}}
     *
     * Enables math calculations on properties from context object
     */
    Handlebars.registerHelper('math', function (lvalue, operator, rvalue) {
        lvalue = parseFloat(lvalue);
        rvalue = parseFloat(rvalue);

        return {
            "+": lvalue + rvalue,
            "-": lvalue - rvalue,
            "*": lvalue * rvalue,
            "/": lvalue / rvalue,
            "%": lvalue % rvalue
        }[operator];
    });

    /**
     * HELPER: formatCurrency
     *
     * Usage: 
     *  1) display currency on property
     *  {{formatCurrency price}}
     *  
     *  2) display currency on calculated property (using math helper)
     *  {{formatCurrency (math price "*" quantity)}}
     *
     *  3) display currency or label when value is 0
     *  {{formatCurrency shipping "Free"}}
     */
    Handlebars.registerHelper('formatCurrency', function (value, freeLabelText) {
        if (typeof freeLabelText === 'string' && !Boolean(value)) {
            return freeLabelText;
        } else {
            return accounting.formatMoney(value);
        }
    });


    // ISO data time formats
    var dateFormats = {
        "default": 'LLL',
        "short": 'MM/DD/YYYY',
        "long": 'MM/DD/YYYY h:mm:ss a'
    };

    /**
     * HELPER: formatDate
     * Usage: 
     *  {{formatDate createDate}}
     *  {{formatDate createDate "short"}}
     *  {{formatDate createDate "MM/DD/YYYY"}}
     *
     * Display data using ISO data time formats or predefined dateFormats array.
     *
    */
    Handlebars.registerHelper('formatDate', function (datetime, options) {
        if (moment) {
            var format = options.hash.format || 'default';
            var outData = moment(datetime);
            format = dateFormats[format] || format;
            if (moment.tz) {
                outData = outData.tz(timezone);
            }
            return moment(outData).format(format);
        }
        else {
            return datetime;
        }
    });

    /**
     * HELPER: formatUtcToLocalDate
     * Usage: 
     *  {{formatUtcToLocalDate createDate}}
     *  {{formatUtcToLocalDate createDate "short"}}
     *  {{formatUtcToLocalDate createDate "MM/DD/YYYY"}}
     *
     * Convert Utc to local dateTime and display data using ISO data time formats or predefined dateFormats array.
     *
    */
    Handlebars.registerHelper('formatUtcToLocalDate', function (datetime, options) {
        if (moment) {
            var format = options.hash.format || 'default';
            var outData = moment.utc(datetime);
            format = dateFormats[format] || format;
            if (moment.tz) {
                outData = outData.tz(timezone);
            }
            return moment(outData).format(format);
        }
        else {
            return datetime;
        }
    });

    Handlebars.registerHelper('isValidCountryForShipping', function (country, options) {
        if (!country || !country.hasOwnProperty('enableShipping') || !country.hasOwnProperty('countryCode')){
            return options.inverse(this);
        }

        if (enableInternationalShipping) {
            return (country.enableShipping === true) ? options.fn(this) : options.inverse(this);
        } else {
            return (country.enableShipping === true && country.countryCode === defaultCountryCode) ? options.fn(this) : options.inverse(this);
        }
    });

    Handlebars.registerHelper('isCurrentYear', function (year, options) {
        var currentYear = moment().year();
        return (parseInt(year) === currentYear) ? options.fn(this) : options.inverse(this);
    });

    Handlebars.registerHelper('isCurrentMonth', function (month, options) {
        var currentMonth = moment().month() + 1;
        return (parseInt(month) === currentMonth) ? options.fn(this) : options.inverse(this);
    });

    Handlebars.registerHelper('formatNumber', function (number, options) {
        var fixed = options.hash.precision || 2;
        number = (number === null || isNaN(number)) ? 0 : number;
        return number.toFixed(fixed);
    });

    Handlebars.registerHelper('formatProductUrl', function (entity, options) {
        var orderItemId = options.hash.orderItemId || null;
        var delimeter = '?';
        var url = entity.productUrl;
        if (entity.groupId) {
            url += delimeter + 'childId=' + entity.productId;
            delimeter = '&';
        }
        if (orderItemId) {
            url += delimeter + 'orderItemId=' +orderItemId;
        }

        return url;
    });

    Handlebars.registerHelper('formatPercent', function (context, options) {
        var fixed = options.hash.fixed || 2;
        return (context * 100).toFixed(fixed) + '%';
    });


    /* Pricing
    - updates here should be also applyed to RocHtmlHelperExtensions | Pricing
    ------------------------------------------------------------------------------*/

    function displayPriceHtml(priceString, strike) {
        strike = strike || false;
        var markup = strike ?
            '<span class="strike">{0}</span>' :
            '<strong class="price" itemprop="price">{0}</strong>';
            
        return markup.replace('{0}', priceString);
    }

    function displayPrice(price, strike) {
        strike = strike || false;
        return displayPriceHtml(accounting.formatMoney(price), strike);
    }

    function displayPriceRange(minPrice, maxPrice, strike) {
        if (minPrice !== null && maxPrice !== null) {
            strike = strike || false;

            if (minPrice === maxPrice) {
                return displayPriceHtml(accounting.formatMoney(minPrice), strike);
            } else {
                var range = accounting.formatMoney(minPrice) + ' - ' + accounting.formatMoney(maxPrice);
                return displayPriceHtml(range, strike);
            }
        }
        return '';
    }

    Handlebars.registerHelper('displayPrice', function (price, options) {
        var strike = options.hash.strike || false;
        return new Handlebars.SafeString(displayPrice(price, strike));
    });

    Handlebars.registerHelper('displayPriceRange', function (minPrice, maxPrice, options) {
        var strike = options.hash.strike || false;
        return new Handlebars.SafeString(displayPriceRange(minPrice, maxPrice, strike));
    });

    /* End: Pricing ----------------------------------------------------------------*/

    Handlebars.registerHelper('nItemsLabel', function () {
        return this.items.length > 1 ? this.items.length + ' Items' : '1 Item';
    });

    Handlebars.registerHelper('nItemsLabel', function (val) {
        return val > 1 ? val + ' Items' : '1 Item';
    });

    Handlebars.registerHelper('nItemsLabelCount', function (val, propName) {
        var counter = 0;
        _.forEach(val, function (i) { counter += typeof i[propName] != 'undefined' ? i[propName] : 0; });
        return counter > 1 ? 'Items (' + counter + ')' : 'Item (1)';
    });

    Handlebars.registerHelper('ccName', function (value) {
        return CreditCard.getCreditCardName(value);
    });

    Handlebars.registerHelper('commalist', function (items) {
        if (typeof items != 'undefined' && items !== null) {
            return items.join(', ');
        }
        return '';
    });

    // HELPER: #ifeq 
    //
    // Usage: {{#ifeq addressType "po-billing"}}
    // 
    // Extended #id Conditional support with specific value
    Handlebars.registerHelper('ifeq', function (a, b, opts) {
        if (a == b) {
            return opts.fn(this);
        }
        return opts.inverse(this);
    });

    // HELPER: #ifCond
    //
    // Usage: 
    // {{#ifCond discount ">" 0}}
    // ...
    // {{else}}
    // ...
    // {{/ifCond}}
    //
    // Conditional statemants support
    Handlebars.registerHelper('ifCond', function (v1, operator, v2, options) {
        switch (operator) {
            case '!=':
                return (v1 != v2) ? options.fn(this) : options.inverse(this);
            case '==':
                return (v1 == v2) ? options.fn(this) : options.inverse(this);
            case '===':
                return (v1 === v2) ? options.fn(this) : options.inverse(this);
            case '<':
                return (v1 < v2) ? options.fn(this) : options.inverse(this);
            case '<=':
                return (v1 <= v2) ? options.fn(this) : options.inverse(this);
            case '>':
                return (v1 > v2) ? options.fn(this) : options.inverse(this);
            case '>=':
                return (v1 >= v2) ? options.fn(this) : options.inverse(this);
            case '&&':
                return (v1 && v2) ? options.fn(this) : options.inverse(this);
            case '||':
                return (v1 || v2) ? options.fn(this) : options.inverse(this);
            default:
                return options.inverse(this);
        }
    });

    // Usage:
    //
    // {{#if (or section1 section2)}}
    //      .. content
    //  {{/if}}
    //
    // Complex logic:
    //  {{#if (or 
    //      (eq section1 "foo")
    //      (ne section2 "bar"))}}
    //      .. content
    //  {{/if}}
    Handlebars.registerHelper({
        eq: function (v1, v2) {
            if (v2 && v2.hash) {
                return !!v1;
            }
            return v1 === v2;
        },
        ne: function (v1, v2) {
            if (v2 && v2.hash) {
                return !v1;
            }
            return v1 !== v2;
        },
        lt: function (v1, v2) {
            return v1 < v2;
        },
        gt: function (v1, v2) {
            return v1 > v2;
        },
        lte: function (v1, v2) {
            return v1 <= v2;
        },
        gte: function (v1, v2) {
            return v1 >= v2;
        },
        and: function (v1, v2) {
            return v1 && v2;
        },
        or: function (v1, v2) {
            return v1 || v2;
        }
    });

    // HELPER: partial
    //
    // Usage:
    // 1) get html without any context object (no bindings) 
    // {{partial "formErrorContainer-Tmp"}}
    //
    // 2) passing object context
    // {{partial "costSummary-Tmp" this}}
    //
    // 3) passing object context extended with inline param
    // {{partial "checkoutSummary-Tmp" this hideCartDetails="true"}}
    //
    // 4) passing list of cutom inline params
    // {{partial "infoBox-Tmp" id="noOrdersMsg" title="You currently don't have any store orders."}}
    //
    // call partial template with various options to provide context object
    Handlebars.registerHelper('partial', function (partialName, context, options) {
        var viewModel = {};
        var params = {};

        if (context && context.hash) {
            params = context.hash;
        } else {
            viewModel = context || {};
            params = options.hash || {};
        }

        $.each(params, function (key, value) {
            viewModel[key] = value;
        });

        return new Handlebars.SafeString(UI.template(partialName, viewModel, true));
    });

    // HELPER: #parseJSON
    //
    // Usage:
    // {{#parseJSON '{"id": "firstname", "label": "First name"}'}}
    //     <div id="{{firstname}}">label</div>
    // {{/parseJSON}}
    //
    // binding data directly in template
    Handlebars.registerHelper('parseJSON', function (data, options) {
        return options.fn(JSON.parse(data));
    });

    Handlebars.registerHelper('parseJSONToObject', function (data, options) {
        return JSON.parse(data);
    });

    // HELPER: getCreditCardCssClass
    //
    // Usage: {{getCreditCardCssClass code}}
    //
    // Returns coresponding css class to credit card code
    Handlebars.registerHelper('getCreditCardCssClass', function (ccCode) {
        return CreditCard.getCreditCardCssClass(ccCode);
    });

    // HELPER: remainingCharacters
    //
    // Usage: {{remainingCharacters 'Remaining: {0} character{1}' comment 250}}
    //
    // returns formatted string with information about remaining characters
    // where:
    // {0} - it will be replaced with number of characters left
    // {1} - optional, you ma want to format your output with singular/plural noun (like "1 character left" or "2 characters left"
    Handlebars.registerHelper('remainingCharacters', function (format, string, maxLength) {
        var left = maxLength - (string === null ? 0 : string.length);
        format = format.replace('{0}', left).replace('{1}', left == 1 ? '' : 's');
        return format;
    });

    // HELPER: getImg
    //
    // Usage: <img src="{{getImg product.defaultImage.path 60 60}}" ...
    //
    // Returns img src based on asset params
    Handlebars.registerHelper('getImg', function (asset, width, height) {
        if (asset !== null) {
            var path = asset;
            if (typeof asset == 'object') {
                if (asset.path !== null) {
                    path = asset.path;
                }
            }
            return Core.getImageUrl({ 'asset': { 'path': path } }, width, height);
        }

        return packagePath + '/assets/images/na-images/na-l.svg'; //fix to handle missing image for products
    });

    // HELPER: capitalize
    //
    // Usage: {{capitalize @key}}
    //
    Handlebars.registerHelper('capitalize', function (str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    });

    /*** object helpers
    ------------------------------------------------------------------------------------*/
    
    /**
     * HELPER: #hasAnyProperties
     * Checks if object is not empty and fails for object like: {}.
     *
     * Usage: {{#hasAnyProperties promotionCodesApplied}} //yes - clouse {{else}} //no - clouse {{/hasAnyProperties}}
     *
     */
    Handlebars.registerHelper('hasAnyProperties', function (obj, opts) {
        for (var key in obj) {
            if (obj.hasOwnProperty(key)) {
                return opts.fn(this);
            }
        }
        return opts.inverse(this);
    });

    /**
     * HELPER: #hasProperty
     * Usage: {{#hasProperty this "baseSubtotal"}} //yes - clouse {{else}} //no - clouse {{/hasProperty}}
     *
     * Checks if object contains property of a given name.
     *
    */
    Handlebars.registerHelper('hasProperty', function (obj, propertyName, opts) {
        for (var key in obj) {
            if (obj.hasOwnProperty(key)) {
                if (key === propertyName) {
                    return opts.fn(this);
                }
            }
        }
        return opts.inverse(this);
    });

   /** 
    * HELPER: #key_value
    *
    * Usage: {{#key_value obj}} Key: {{key}} // Value: {{value}} {{/key_value}}
    *
    * Iterate over an object, setting 'key' and 'value' for each property in
    * the object.
    */
    Handlebars.registerHelper('key_value', function (obj, fn) {
        var buffer = '',
            key;

        for (key in obj) {
            if (obj.hasOwnProperty(key)) {
                buffer += fn({ key: key, value: obj[key] });
            }
        }

        return buffer;
    });

    // HELPER: #each_with_key
    //
    // Usage: {{#each_with_key container key="myKey"}}...{{/each_with_key}}
    //
    // Iterate over an object containing other objects. Each
    // inner object will be used in turn, with an added key ("myKey")
    // set to the value of the inner object's key in the container.
    Handlebars.registerHelper('each_with_key', function (obj, fn) {
        var context,
            buffer = '',
            key,
            keyName = fn.hash.key;

        for (key in obj) {
            if (obj.hasOwnProperty(key)) {
                context = obj[key];

                if (keyName) {
                    context[keyName] = key;
                }

                buffer += fn(context);
            }
        }

        return buffer;
    });

    // HELPER: #each_when
    //
    // Usage: {{#each_when profile "gender" "female"}} ... {{/each_when}}
    //
    Handlebars.registerHelper('each_when', function(list, k, v, opts) {
        var i, result = '';
        for (i = 0; i < list.length; ++i) {
            if (list[i][k] == v) {
                result = result + opts.fn(list[i]);
            }
        };
        return result;
    });

    Handlebars.registerHelper('debug', function (value) {
        Logger.info('* * *');
        Logger.info('Context: ', this);
        if (value) {
            Logger.info('Value: ', value);
        }
        return;
    });

    Handlebars.registerHelper('log', function (value) {
        return Logger.log(value);
    });

    Handlebars.registerHelper('info', function (value) {
        return Logger.info(value);
    });

    Handlebars.registerHelper('warn', function (value) {
        return Logger.warn(value);
    });

    Handlebars.registerHelper('error', function (value) {
        return Logger.error(value);
    });

    Handlebars.registerHelper('extendViewModel', function (key, value) {
        var viewModel = this;
        viewModel[key] = value;
        Logger.info('extendViewModel:' + key + ' -> ' + value);
        return Logger.info(viewModel);
    });

    Handlebars.registerHelper('dayOfWeekAbbrevation', function (dayOfWeek) {
        var abbreviation = dayOfWeek.substring(0, 3);
        return new Handlebars.SafeString(abbreviation);
    });
    
    Handlebars.registerHelper('showDeliveryRadioButton', function (deliveryOptions) {
        var statuses = ['Available', 'Backorder'];
        var shippingAvailable = statuses.indexOf(deliveryOptions.inventoryAvailability.shippingSummary.status) > -1;
        var pickupAvailable = deliveryOptions.inventoryAvailability.pickupDetails !== null && statuses.indexOf(deliveryOptions.inventoryAvailability.pickupDetails.inventorySummary.status) > -1;
        return (deliveryOptions.inStorePickupEnabled && (shippingAvailable || pickupAvailable));
    });

    Handlebars.registerHelper('isNotEmptyGuid', function(guid) {
        return typeof guid !== 'undefined' && guid !== Constants.enums.general.DEFAULT_GUID;
    });

    // example data-roc-params="{{createRocParams addressType=addressType}}"
    Handlebars.registerHelper('createDataAttributeJSON', function (options) {
        var attributes = {};

        if (options.hash) {
            for (var attributeName in options.hash) {
                attributes[attributeName] = options.hash[attributeName];
            }
        } else {
            attributes = options;
        };

        return JSON.stringify(attributes);
    });

    return Handlebars;
});

